'use client';

import { useEffect, useRef } from 'react';
import { TUTORIAL_FLOOR_INDEX } from './tutorial-board';

/**
 * Robust click / key blocker for the tutorial.
 *
 * The previous setup relied on four invisible "blocker rects" rendered
 * around the spotlight cutout, plus a flat dim layer with `swallow`
 * handlers. That worked when:
 *   - a step had a `rect` AND `dim=true` (rects fired)
 *   - a step had no rect AND `dim=true` (flat dim fired)
 *
 * It silently leaked everywhere else: `own-tile-blue`,
 * `competitor-buyouts`, `path-leave`, `path-defend`, `set-price-rent`
 * all run with `dim=false`, so users could click any HUD panel,
 * adjacent tile, header item, or — worst — the buyout drawer's close
 * button mid-step and break the script. CSS `pointer-events` blocking
 * is also gappy during react-portal transitions and animation frames
 * because the blockers depend on the spotlight rect being committed.
 *
 * This hook is the canonical defence: it installs a capture-phase
 * listener at `window` for every interactive event type and decides
 * via DOM containment whether the event came from an allowed origin:
 *
 *   1. The tutorial overlay portal itself (Skip / Next / branch
 *      buttons / outro CTAs all live there).
 *   2. The currently-highlighted DOM element + its descendants
 *      (the 1.2x preset chip, the Take Over button, the My Tiles
 *      panel, the rewards pill, etc.). Re-resolved on every event so
 *      remounts (Radix drawer slide-in) don't open a blocking gap.
 *   3. The `<canvas>` element inside `[data-board-canvas]`, *only*
 *      when the script is asking the user to click the floor tile (3D
 *      mode has no DOM tile to contain the canvas click). The
 *      seat-level filter in `LobbyClient.openDrawerForSeat` then
 *      narrows the canvas allowance to the floor seat — clicking any
 *      other tile is a no-op.
 *
 *      CRITICAL: we restrict the allow-list to the actual `<canvas>`
 *      tag rather than the `[data-board-canvas]` wrapper. The wrapper
 *      contains `ChatPanel`, `BoardListPanel`, `PinnedTitlePanel`,
 *      `MyTilesPanel`, etc. as absolutely-positioned siblings of the
 *      canvas — allowing the wrapper would let the user chat, change
 *      board, and click any HUD panel during `find-floor`. That was
 *      the original "I can send messages in chat during a tile step"
 *      bug.
 *
 * Anything else gets `stopImmediatePropagation` + `preventDefault`,
 * which kills the event before any React or Radix handler can see it
 * — independent of CSS pointer-events, z-index, or which side of an
 * animation frame we're on.
 *
 * # Keyboard handling
 *
 * `stopPropagation` halts the event traversal but does NOT prevent
 * the user-agent default action — a `keydown` stopped at window
 * capture STILL inserts a character into a focused `<input>` because
 * the "type a character" default is the UA's job, not the event
 * target's. So we explicitly `preventDefault` every cancelable
 * keyboard event (`keydown`, `keypress`, `beforeinput`, `input`,
 * IME composition events). Combined with the focus interception
 * below this means a chat input that was somehow focused can't
 * receive characters even if the focus shift itself slipped through.
 *
 * # Focus management
 *
 * Even with every click blocked, a `<input>` focused before the
 * tutorial mounted (or one that slipped through during a stale
 * highlight tick) will keep receiving keystrokes from the keyboard
 * unless we both:
 *
 *   a) preventDefault on `keydown` etc. (above), AND
 *   b) actively blur the offender so the user can't even see the
 *      caret blinking.
 *
 * On hook mount + every highlight change we walk
 * `document.activeElement` — if it's not in the allow-list, blur it.
 * On every `focusin` we re-check; non-allowed focuses are blurred on
 * the next microtask (calling `blur` synchronously inside the focus
 * handler can cause Safari to re-focus body and loop). The result is
 * that focus is pinned to the overlay portal / highlighted element
 * for the duration of the tutorial.
 */

/** Event types the guard intercepts. We deliberately omit `wheel`
 *  (scrolling is fine) and `pointermove`/`mousemove` (we don't want
 *  to freeze hover state — adding overhead to every move would be
 *  expensive too). Keyboard + composition + paste/cut/drag events
 *  block keystroke-driven activation, IME-driven character insertion,
 *  paste-into-focused-input, and drag-and-drop into the chat. */
const GUARDED_EVENTS = [
  'pointerdown',
  'pointerup',
  'mousedown',
  'mouseup',
  'click',
  'dblclick',
  'auxclick',
  'contextmenu',
  'touchstart',
  'touchend',
  'keydown',
  'keypress',
  'keyup',
  'beforeinput',
  'input',
  'compositionstart',
  'compositionupdate',
  'compositionend',
  'submit',
  'paste',
  'cut',
  'drop',
  'dragstart',
] as const satisfies ReadonlyArray<keyof WindowEventMap>;

/** Targets that point at a board tile rather than a HUD panel/button.
 *  Mirrored from `TutorialOverlay.isTileTarget` — kept local here so
 *  the guard module doesn't import the overlay (would cause a circular
 *  dep). */
function isTileTarget(target: string | null | undefined): boolean {
  if (!target) return false;
  return target === 'tutorial-floor-tile' || target.startsWith('tutorial-tile-');
}

/** Live resolver for the highlighted element. We re-query each event
 *  rather than caching because the buyout drawer's preset chips mount
 *  ~250 ms after the drawer slides in — caching the lookup at hook-
 *  effect time would leave the chip's first paint blocked. */
function resolveHighlightedElement(target: string | null): HTMLElement | null {
  if (!target) return null;
  if (typeof document === 'undefined') return null;
  return (
    document.querySelector<HTMLElement>(`[data-tutorial-target="${target}"]`) ??
    document.querySelector<HTMLElement>(`[data-hud-id="${target}"]`) ??
    (target === 'tutorial-floor-tile'
      ? document.querySelector<HTMLElement>(`[data-seat-index="${TUTORIAL_FLOOR_INDEX}"]`)
      : null)
  );
}

/** Walk an `EventTarget` up to its first `Element` ancestor. Inputs
 *  inside contenteditables can fire events with a `Text` node target,
 *  which doesn't have `.contains` — climbing one parent gets us to a
 *  proper Element node we can run containment checks against. */
function asElement(rawTarget: EventTarget | null): Element | null {
  if (!rawTarget) return null;
  let node: Node | null = rawTarget instanceof Node ? rawTarget : null;
  while (node && !(node instanceof Element)) {
    node = node.parentNode;
  }
  return node as Element | null;
}

/**
 * Detects keyboard events that should pass straight through the guard
 * because they're system-level browser shortcuts the user reasonably
 * expects to keep working at any time — refresh, devtools, URL bar
 * focus, tab switching, Escape, etc.
 *
 * The original guard preventDefault'd every cancelable keydown so chat
 * inputs couldn't be typed into. That worked for the chat case but
 * also swallowed Cmd-R / Ctrl-R, F5, F12, Cmd-L, Cmd-Shift-I — which
 * makes the tutorial feel like a hostile fullscreen modal rather than
 * a guided lobby. The user explicitly flagged this ("commands like
 * ctrl+R to refresh page etc need to work on this screen"), so we
 * treat any of the following as browser-owned and let them through:
 *
 *   - Function keys F1-F12 (refresh, devtools, fullscreen toggle, etc.)
 *   - `Escape` (exit fullscreen, dismiss native dialogs)
 *   - Any keypress with `metaKey` (Cmd on macOS) or `ctrlKey` modifier
 *     (Linux/Windows refresh, devtools, save-as, …). Modifier-only
 *     keystrokes don't insert characters into focused inputs, so we
 *     don't lose the chat-input lockdown by allowing them.
 *
 * Plain keystrokes (typing letters / numbers / arrow keys) are still
 * intercepted — that's what stops the chat input from receiving
 * characters when the user has somehow focused it.
 */
function isBrowserShortcut(event: Event): boolean {
  if (typeof KeyboardEvent === 'undefined' || !(event instanceof KeyboardEvent)) {
    return false;
  }
  const { key } = event;
  if (/^F\d{1,2}$/.test(key)) return true;
  if (key === 'Escape') return true;
  if (event.metaKey || event.ctrlKey) return true;
  return false;
}

interface UseTutorialInputGuardOptions {
  /** Root element of the tutorial overlay portal. Events targeted at
   *  any descendant pass through (this is the dialogue panel, Skip
   *  button, branch CTAs, outro Lobby/Play Again CTAs). */
  overlayRoot: HTMLElement | null;
  /** Currently-spotlit `data-tutorial-target` / `data-hud-id` value.
   *  Looked up live on each event so DOM remounts don't block. */
  highlightTarget: string | null;
  /** When true, events targeting the `<canvas>` inside
   *  `[data-board-canvas]` are allowed through. The seat-level filter
   *  in `LobbyClient.openDrawerForSeat` further restricts the canvas
   *  allowance to the tutorial floor tile — non-floor tile clicks
   *  reach the bridge but get rejected before opening a drawer. */
  allowBoardCanvas: boolean;
}

export function useTutorialInputGuard({
  overlayRoot,
  highlightTarget,
  allowBoardCanvas,
}: UseTutorialInputGuardOptions): void {
  // Stash the latest values in a ref so the listener installed below
  // doesn't have to re-bind on every change. Capture-phase listeners
  // bound to `window` are cheap to leave installed but expensive to
  // re-register (each re-bind triggers an internal map rebalance in
  // some browsers).
  const ctxRef = useRef({ overlayRoot, highlightTarget, allowBoardCanvas });
  ctxRef.current = { overlayRoot, highlightTarget, allowBoardCanvas };

  // ── Tutorial-active body marker.
  // CSS in `globals.css` keys off `body[data-tutorial-active]` to
  // disable text selection on the lobby chrome and add a thin
  // pointer-events fence as a third defence layer. The JS guard is
  // the source of truth; the body marker just lets CSS pre-emptively
  // dim interactivity for the (very) brief windows when the JS
  // listeners haven't bound yet.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const body = document.body;
    body.setAttribute('data-tutorial-active', 'true');
    return () => {
      body.removeAttribute('data-tutorial-active');
    };
  }, []);

  // ── Initial blur + blur on highlight change.
  // If the user (or a previous app session in the same tab) left
  // focus on a chat input before the tutorial mounted, keystrokes
  // would still type into it because focus is sticky. Blur on every
  // step transition so the chat input loses its caret the instant
  // we move into a step that doesn't allow it.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const active = document.activeElement;
    if (!active || !(active instanceof HTMLElement)) return;
    if (isAllowedElement(active, ctxRef.current)) return;
    // Defer blur to the next microtask so we don't fight whichever
    // commit phase put focus there in the first place. `try/catch`
    // around `.blur()` because Safari can throw on detached nodes.
    queueMicrotask(() => {
      try {
        active.blur();
      } catch {
        /* no-op */
      }
    });
  }, [highlightTarget]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handler = (event: Event) => {
      // System-level browser shortcuts (Cmd-R, F5, F12, Cmd-L, Esc,
      // …) get to do their thing — see `isBrowserShortcut` for the
      // full whitelist. Returning before any propagation/preventDefault
      // call means the user-agent's own keydown handler still runs.
      if (isBrowserShortcut(event)) return;
      if (isAllowedElement(asElement(event.target), ctxRef.current)) return;
      // `stopImmediatePropagation` is the only one that kills sibling
      // capture-phase listeners attached to the same window. Without
      // it, Radix's own focus-trap handlers (which also bind in
      // capture) would still close drawers on Esc.
      event.stopPropagation();
      if (
        typeof (event as Event & { stopImmediatePropagation?: () => void })
          .stopImmediatePropagation === 'function'
      ) {
        (event as Event & { stopImmediatePropagation: () => void }).stopImmediatePropagation();
      }
      // preventDefault every cancelable event — keyboard included.
      // Without preventDefault the user-agent will still type a
      // character into a focused input even if the keydown event was
      // stopPropagation'd at window capture (the "type a character"
      // action is the UA's job, not the target's). Same applies to
      // paste / drop / submit — propagation control alone leaves the
      // default action enabled.
      if (event.cancelable) {
        event.preventDefault();
      }
    };

    // Focus interception. Capture-phase `focusin` runs before any
    // descendant focus handler, so we can detect focus moving to a
    // chat input the moment it happens. Focus events aren't
    // cancelable, so we can't preventDefault — the only correctable
    // action is to blur on the next microtask. (Calling blur
    // synchronously can re-focus body and trigger another focusin →
    // infinite loop on Safari/WebKit.)
    const focusHandler = (event: FocusEvent) => {
      const target = asElement(event.target);
      if (!target || !(target instanceof HTMLElement)) return;
      if (isAllowedElement(target, ctxRef.current)) return;
      queueMicrotask(() => {
        try {
          target.blur();
        } catch {
          /* no-op */
        }
      });
    };

    GUARDED_EVENTS.forEach((evt) => {
      window.addEventListener(evt, handler, { capture: true });
    });
    window.addEventListener('focusin', focusHandler, { capture: true });
    return () => {
      GUARDED_EVENTS.forEach((evt) => {
        window.removeEventListener(evt, handler, { capture: true });
      });
      window.removeEventListener('focusin', focusHandler, { capture: true });
    };
    // Empty deps: handler reads from `ctxRef.current` so it always
    // sees the latest target/overlayRoot/allowBoardCanvas without us
    // re-binding window listeners on every step transition (which
    // would briefly drop the guard between unmount and mount).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

/** Containment check for the guard. Lifted out so the
 *  `useEffect`-driven initial blur and the live event handler can
 *  share the same allow-list logic — keeping them in sync was a
 *  source of subtle bugs in the previous version (the blur effect
 *  used a slightly different allow-list and would blur elements the
 *  guard considered allowed). */
function isAllowedElement(
  target: Element | null,
  ctx: {
    overlayRoot: HTMLElement | null;
    highlightTarget: string | null;
    allowBoardCanvas: boolean;
  },
): boolean {
  if (!target) return false;

  // 1. Tutorial overlay (dialogue panel + Skip + branch + outro
  //    CTAs all live inside the portal root).
  if (ctx.overlayRoot && ctx.overlayRoot.contains(target)) return true;

  // 2. Currently-highlighted element. Re-resolved on every event
  //    so the buyout drawer's deferred-mount preset chip becomes
  //    interactive the moment Radix paints it, without waiting for
  //    a React effect to re-bind the listener.
  const highlighted = resolveHighlightedElement(ctx.highlightTarget);
  if (highlighted && highlighted.contains(target)) return true;

  // 3. R3F `<canvas>` element — only when the script is asking for a
  //    tile click. 3D mode has no DOM for individual tiles, so the
  //    highlight resolves to an invisible 0×0 tracker overlay
  //    (`pointer-events: none`); the actual click lands on the
  //    `<canvas>`. `closest('canvas')` lets the raycaster receive
  //    the pointer; the bridge in `LobbyClient.openDrawerForSeat`
  //    then rejects anything that isn't the tutorial floor seat.
  //
  //    Critically we restrict to the `<canvas>` tag, *not* the
  //    `[data-board-canvas]` wrapper. The wrapper also contains
  //    `ChatPanel`, `BoardListPanel`, `PinnedTitlePanel`,
  //    `MyTilesPanel`, etc. as absolutely-positioned siblings of
  //    the canvas — allowing the wrapper let users click chat,
  //    change board, and click any HUD panel during `find-floor`.
  //    The "I can send messages in chat" bug.
  if (ctx.allowBoardCanvas) {
    const canvas = target.closest('canvas');
    if (canvas) {
      const wrapper = document.querySelector<HTMLElement>('[data-board-canvas]');
      if (wrapper && wrapper.contains(canvas)) return true;
    }
  }

  // 4. Audio controls (SFX / Music toggles in `LobbyFooter`).
  //    The user can already mute/unmute audio before starting the
  //    tutorial, but the polished script + buyout SFX can be
  //    surprising mid-flow, so we keep these pills hot-wired even
  //    while the rest of the lobby is locked down. The
  //    `data-tutorial-audio-controls` marker lives on the
  //    LobbyFooter wrapper around both toggle pills — `closest`
  //    picks up clicks on the toggle button itself plus its child
  //    icon / popover trigger.
  if (target.closest('[data-tutorial-audio-controls]')) return true;

  return false;
}

export { isTileTarget as isTutorialTileTarget };
